import { useIonAlert } from '@ionic/react';
import {
  isClientError,
  MutationWithContext,
  retryAllFailedMutations,
} from '@lidea/shared/data-access/core';
import { enterManualOffline } from '@lidea/shared/feature-offline';
import { usePrevious } from '@mantine/hooks';
import { onlineManager, useMutationState, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getSlowMutationThresholdMs,
  isMutationStillStruggling,
  isSyncStruggleMutation,
  supportsOfflineQueue,
} from '../utils/mutation-struggle-policy';
import classes from './use-mutations-alerts.module.css';

const SLOW_SPAM_COOLDOWN_MS = 30_000;
const ERROR_SPAM_COOLDOWN_MS = 30_000;
/** After reconnect, let paused mutations resume without instantly suggesting offline mode. */
const RECONNECT_ALERT_GRACE_MS = 15_000;

const timerSlotKey = (mutation: MutationWithContext) =>
  `${mutation.mutationId}:${mutation.state.failureCount ?? 0}`;

export const useMutationsAlerts = () => {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const queryClient = useQueryClient();
  const [presentAlert, dismissAlert] = useIonAlert();
  const timersRef = useRef<Record<string, NodeJS.Timeout>>({});
  const pendingStruggleIdsRef = useRef<Set<number>>(new Set());
  const reconnectGraceUntilRef = useRef(0);
  const lastAlertRef = useRef<Record<string, number>>({
    slow: 0,
    error: 0,
  });
  const firstMountRef = useRef<boolean>(true);

  const isInReconnectGrace = () => Date.now() < reconnectGraceUntilRef.current;

  useEffect(() => {
    return onlineManager.subscribe((isOnline) => {
      if (isOnline) {
        reconnectGraceUntilRef.current = Date.now() + RECONNECT_ALERT_GRACE_MS;
      }
    });
  }, []);

  const pendingStruggleMutations = useMutationState({
    filters: {
      status: 'pending',
      mutationKey: [],
      predicate: (mutation) =>
        isSyncStruggleMutation(mutation.options.mutationKey) &&
        !mutation.state.isPaused,
    },
    select: (mutation) => mutation as MutationWithContext,
  });

  const failedQueueMutations = useMutationState({
    filters: {
      status: 'error',
      mutationKey: [],
      predicate: (mutation) =>
        supportsOfflineQueue(mutation.options.mutationKey) &&
        !isClientError(mutation.state.error),
    },
    select: (mutation) => mutation as MutationWithContext,
  });

  const previousFailedMutationsLength = usePrevious(failedQueueMutations.length);

  const handleSlowMutation = useCallback(
    (mutationId: number) => {
      const mutation = queryClient.getMutationCache().find({
        predicate: (entry) => entry.mutationId === mutationId,
      }) as MutationWithContext | undefined;

      if (!mutation || !isMutationStillStruggling(mutation)) {
        return;
      }

      if (!onlineManager.isOnline() || isInReconnectGrace()) {
        return;
      }

      const now = Date.now();
      if (now - lastAlertRef.current.slow <= SLOW_SPAM_COOLDOWN_MS) {
        return;
      }

      lastAlertRef.current.slow = now;

      presentAlert({
        header: t('common:mutation_alert.slow_header'),
        message: t('common:mutation_alert.offline_info'),
        buttons: [
          {
            text: t('common:mutation_alert.dont_go_offline'),
            role: 'cancel',
            cssClass: classes.errorAlertCancelButton,
          },
          {
            text: t('common:mutation_alert.go_offline'),
            role: 'confirm',
            handler: () => {
              enterManualOffline();
            },
          },
        ],
      });
    },
    [presentAlert, queryClient, t]
  );

  const handleFailedMutations = useCallback(() => {
    if (!onlineManager.isOnline() || isInReconnectGrace()) {
      return;
    }

    const now = Date.now();
    if (now - lastAlertRef.current.error <= ERROR_SPAM_COOLDOWN_MS) {
      return;
    }

    lastAlertRef.current.error = now;
    dismissAlert();
    presentAlert({
      cssClass: classes.errorAlert,
      header: t('common:mutation_alert.error_header'),
      message: t('common:mutation_alert.offline_info'),
      buttons: [
        {
          text: t('common:mutation_alert.dont_go_offline'),
          role: 'cancel',
          cssClass: classes.errorAlertCancelButton,
        },
          {
            text: t('common:mutation_alert.go_offline'),
            role: 'confirm',
            cssClass: classes.errorAlertConfirmButton,
            handler: () => {
              enterManualOffline();
              retryAllFailedMutations(queryClient);
            },
          },
      ],
    });
  }, [dismissAlert, presentAlert, queryClient, t]);

  useEffect(() => {
    const activeTimerKeys = new Set<string>();
    const previousPendingIds = pendingStruggleIdsRef.current;

    for (const mutation of pendingStruggleMutations) {
      const slotKey = timerSlotKey(mutation);
      activeTimerKeys.add(slotKey);

      if (timersRef.current[slotKey]) {
        continue;
      }

      const threshold = getSlowMutationThresholdMs(mutation);
      if (threshold === null) {
        continue;
      }

      const submittedAt = mutation.state.submittedAt ?? Date.now();
      const elapsed = Date.now() - submittedAt;
      let remaining = Math.max(threshold - elapsed, 0);
      // Stale submittedAt from offline queue would fire the alert instantly on resume.
      if (
        remaining === 0 &&
        !previousPendingIds.has(mutation.mutationId)
      ) {
        remaining = threshold;
      }

      timersRef.current[slotKey] = setTimeout(() => {
        handleSlowMutation(mutation.mutationId);
      }, remaining);
    }

    pendingStruggleIdsRef.current = new Set(
      pendingStruggleMutations.map((mutation) => mutation.mutationId)
    );

    Object.keys(timersRef.current).forEach((slotKey) => {
      if (!activeTimerKeys.has(slotKey)) {
        clearTimeout(timersRef.current[slotKey]);
        delete timersRef.current[slotKey];
      }
    });

    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
      timersRef.current = {};
    };
  }, [pendingStruggleMutations, handleSlowMutation]);

  useEffect(() => {
    if (firstMountRef.current) {
      firstMountRef.current = false;
      return;
    }

    const isNewFailedMutation =
      typeof previousFailedMutationsLength === 'number' &&
      failedQueueMutations.length >= previousFailedMutationsLength;

    if (
      failedQueueMutations.length === 0 ||
      !isNewFailedMutation ||
      typeof previousFailedMutationsLength !== 'number' ||
      failedQueueMutations.length === previousFailedMutationsLength
    ) {
      return;
    }

    // Wait past the post-settled cache cleanup before alerting.
    const timer = setTimeout(() => {
      const stillFailing = queryClient
        .getMutationCache()
        .findAll({
          status: 'error',
          predicate: (mutation) =>
            supportsOfflineQueue(mutation.options.mutationKey) &&
            !isClientError(mutation.state.error),
        });

      if (stillFailing.length > 0) {
        handleFailedMutations();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [
    handleFailedMutations,
    failedQueueMutations.length,
    previousFailedMutationsLength,
    queryClient,
  ]);

  return null;
};
